import { useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { custom, z } from "zod";
import { Decimal } from "decimal.js";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
} from "../../utils";
import { PossibleRole } from "@/lib/permissions/stytch_original";
import { Commodities } from "../../commodity";
import { transformToDecimal } from "../../common";
import { BaseDbObject } from "@/lib/models/db";

export const CashSalesFiltersValidation = z.object({
  is_enabled: z.coerce.boolean().default(true),
  customer_id: z.string().uuid().optional(),
  trader_id: z.string().uuid().optional(),
  location_id: z.string().uuid().optional(),
  market_zone_id: z.string().uuid().optional(),
  commodity: z.string().optional(),
  crop_year: z.coerce.number().optional(),
  contract_number: z.string().optional(),
  option_month: z.string().optional(),
  contract_type: z.string().optional(),
  pr_status: z.string().optional(),
  futures: z.string().optional(),
  basis: z.string().optional(),
  range_start: z.string().optional(),
  range_end: z.string().optional(),
  // range of start / end dates
});

export const ProcessStagedValidation = z.object({
  staged_id: z.string().uuid(),
  existing_id: z.string().uuid().optional(),
});

export interface RiskManagementCashSales extends BaseDbObject {
  contract_type: string;
  contract_number: string;
  contract_release: string;
  pr_status: string;
  compound_id: string;

  location_id: string;
  customer_id: string;
  market_zone_id: string;
  trader_id: string;

  start_date: Date;
  end_date: Date;

  commodity: string;
  commodity_type: string;
  crop_year: string;
  option_month: string;

  ct_uom?: string;
  pr_uom?: string;
  uom?: string;

  quantity: number;
  balance: number;
  futures?: number;
  basis?: number;

  Customer?: {
    id: string;
    long_name: string;
  }
  Location?: {
    id: string;
    long_name: string;
  }
  MarketZone?: {
    id: string;
    dtn_market_zone: string;
  },
  Trader?: {
    id: string;
    initials: string;
    name: string;
  }
}


export interface RiskManagementCashSalesHistory extends RiskManagementCashSales {
  cash_sale_id: string;
  history_type: string;
}


export interface ListCashSalesResult {
  cash_sales: RiskManagementCashSales[]
}

export function useListRiskCashSales({ filters }: { filters?: z.infer<typeof CashSalesFiltersValidation> }) {
  const queryParams = new URLSearchParams();

  if (filters) {
    if (filters.contract_number) {
      queryParams.set("contract_number", filters.contract_number);
    }
    if (filters.commodity) {
      queryParams.set("commodity", filters.commodity);
    }
    if (filters.location_id) {
      queryParams.set("location_id", filters.location_id);
    }
    if (filters.option_month) {
      queryParams.set("option_month", filters.option_month);
    }
    if (filters.is_enabled !== undefined) {
      queryParams.set("is_enabled", filters.is_enabled.toString());
    }
    if (filters.contract_type) {
      queryParams.set("contract_type", filters.contract_type);
    }
    if (filters.customer_id) {
      queryParams.set("customer_id", filters.customer_id);
    }
    if (filters.market_zone_id) {
      queryParams.set("market_zone_id", filters.market_zone_id);
    }
    if (filters.crop_year) {
      queryParams.set("crop_year", filters.crop_year?.toString());
    }
    if (filters.trader_id) {
      queryParams.set("trader_id", filters.trader_id);
    }
    if (filters.range_start) {
      queryParams.set("range_start", moment(filters.range_start).format('YYYY-MM-DD'));
    }
    if (filters.range_end) {
      queryParams.set("range_end", moment(filters.range_end).format('YYYY-MM-DD'));
    }
  }

  return useQuery({
    queryKey: ["risk", "cash_sales", "top_level", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<ListCashSalesResult>("risk/management/cash_sales", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useListStagedCashSales() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["risk", "cash_sales", "staged"],
    queryFn: () =>
      fetchApiGet<ListCashSalesResult>("risk/management/cash_sales/staged", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useProcessStagedRow() {
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams();

  return async (data: z.infer<typeof ProcessStagedValidation>) => {
    const result = await fetchApiPost<{}>(
      `risk/management/cash_sales/staged/process`,
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "cash_sales"] });
    }

    return result;
  };
}

export function useDeleteStagedRow() {
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams();

  return async (id: string) => {
    const result = await fetchApiDelete<{}>(
      `risk/management/cash_sales/staged/${id}`,
      queryParams,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "cash_sales", "staged"] });
    }

    return result;
  };
}

export function useDeleteCashSaleRow() {
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams();

  return async (id: string) => {
    const result = await fetchApiDelete<{}>(
      `risk/management/cash_sales/normal/${id}`,
      queryParams,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "cash_sales"] });
    }

    return result;
  };
}
